import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, X, Copy, Download, Search, Timer, Trash2, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useProject } from '@/lib/ProjectContext';
import { getCurrentUser } from '@/lib/auth';
import ContactSelect from '@/components/time/ContactSelect';
import { timeAPI, fmtMoney, fmtDay, todayISO, addDaysISO, openBlob, PERIODS, periodParams, thisMonthISO, parseHours, fmtHours, rateFor } from '@/lib/financeAPI';

const red = 'bg-[#A62B1F] hover:bg-[#8C2318] text-white';
const card = 'rounded-xl border border-[#E3DDD1] bg-white';
const serif = { fontFamily: 'Fraunces, Georgia, serif' };
const sel = 'w-full h-10 rounded-md border border-input px-2 text-sm bg-white';
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);
const isAdmin = () => ['owner', 'admin'].includes(getCurrentUser()?.role) || getCurrentUser()?.is_super_admin;
const changed = () => window.dispatchEvent(new Event('time-changed'));

const Tile = ({ label, value, note }) => (
  <div className={`${card} p-4`}>
    <div className="text-[11px] uppercase tracking-wider text-[#5C6670] font-semibold">{label}</div>
    <div className="text-xl font-semibold mt-1 text-[#1E2A33]">{value}</div>
    {note && <div className="text-xs text-[#5C6670] mt-0.5">{note}</div>}
  </div>
);


const PeriodPicker = ({ value, onChange }) => (
  <div className="flex gap-2 flex-wrap items-center">
    <select className="h-9 rounded-md border border-input px-2 text-sm bg-white" value={value.period} onChange={(e) => onChange({ ...value, period: e.target.value, month: value.month || thisMonthISO() })}>
      {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
      <option value="all">All dates</option>
    </select>
    {value.period === 'month' && (
      <Input type="month" className="h-9 w-44" value={value.month || thisMonthISO()} max={thisMonthISO()} onChange={(e) => onChange({ ...value, month: e.target.value })} />
    )}
    {value.period === 'custom' && (
      <>
        <Input type="date" className="h-9 w-40" value={value.from_ || ''} onChange={(e) => onChange({ ...value, from_: e.target.value })} />
        <span className="text-xs text-[#5C6670]">to</span>
        <Input type="date" className="h-9 w-40" value={value.to || ''} onChange={(e) => onChange({ ...value, to: e.target.value })} />
      </>
    )}
  </div>
);

// ── Edit one entry ───────────────────────────────────────────────────────────
const EntryDialog = ({ open, onOpenChange, entry, settings, onSaved }) => {
  const { projects = [] } = useProject() || {};
  const [f, setF] = useState(null);
  // Works from its own copy (f): the page clears `entry` the moment the dialog closes, while it's still on screen.
  useEffect(() => {
    if (open && entry) setF({ ...entry, hoursText: String(entry.hours), project_id: entry.project_id || '', contact_id: entry.contact_id || '', rateText: String(entry.rate ?? '') });
  }, [open, entry]);
  if (!f) return null;
  const set = (patch) => setF({ ...f, ...patch });
  const billed = !!f.invoice_id;
  const save = async () => {
    const hours = parseHours(f.hoursText);
    if (!hours) return toast.error('Enter the hours, e.g. 1.5 or 1:30');
    const body = { date: f.date, activity: f.activity, description: f.description, project_id: f.project_id || '', contact_id: f.contact_id || '' };
    if (!billed) { body.hours = hours; body.billable = f.billable; if (Number(f.rateText) !== Number(f.rate)) body.rate = Number(f.rateText || 0); }
    try { const r = await timeAPI.update(f.id, body); toast.success('Saved'); onOpenChange(false); onSaved(r.data); changed(); } catch (e) { err(e, 'Could not save'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Edit time</DialogTitle>
          <DialogDescription>{billed ? `Billed on invoice ${f.invoice_number}. You can change the note and tags; take it off the invoice to change hours or rate.` : `Priced at ${fmtMoney(f.rate)}/h (${f.rate_source || 'rate'}).`}</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <Input type="date" value={f.date} onChange={(e) => set({ date: e.target.value })} />
            <Input value={f.hoursText} disabled={billed} onChange={(e) => set({ hoursText: e.target.value })} placeholder="Hours" />
            <Input type="number" step="0.01" value={f.rateText} disabled={billed} onChange={(e) => set({ rateText: e.target.value })} title="Hourly rate" />
          </div>
          <Input list="time-activities" value={f.activity} onChange={(e) => set({ activity: e.target.value })} placeholder="Activity" />
          <Input value={f.description} onChange={(e) => set({ description: e.target.value })} placeholder="What you did" />
          <select className={sel} value={f.project_id} onChange={(e) => set({ project_id: e.target.value })}>
            <option value="">No project</option>{projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
          <ContactSelect value={f.contact_id} label={f.contact_name} onChange={(c) => set({ contact_id: c?.id || '', contact_name: c?.label || '' })} />
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.billable} disabled={billed} onCheckedChange={(v) => set({ billable: v })} />Billable</label>
          <p className="text-[11px] text-[#94A3B8]">Changing the project or client re-prices it at that rate unless you've typed your own.</p>
          <Button className={`w-full ${red}`} onClick={save}>Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Timesheet ────────────────────────────────────────────────────────────────
const blankQuick = () => ({ date: todayISO(), hoursText: '', activity: '', description: '', project_id: '', contact_id: '', contact_name: '', billable: true });

const Timesheet = ({ settings }) => {
  const { projects = [] } = useProject() || {};
  const [period, setPeriod] = useState({ period: 'this_month' });
  const [projectId, setProjectId] = useState('');
  const [billable, setBillable] = useState('');
  const [unbilled, setUnbilled] = useState(false);
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [quick, setQuick] = useState(blankQuick());
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => {
    timeAPI.list({ ...periodParams(period), project_id: projectId || undefined, billable: billable || undefined, unbilled: unbilled || undefined, q: q || undefined })
      .then((r) => setData(r.data)).catch((e) => err(e, 'Could not load time'));
  }, [period, projectId, billable, unbilled, q]);
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { window.addEventListener('time-changed', load); return () => window.removeEventListener('time-changed', load); }, [load]);

  const add = async () => {
    const hours = parseHours(quick.hoursText);
    if (!hours) return toast.error('Enter the hours, e.g. 1.5, 1:30 or 90m');
    try {
      await timeAPI.create({ date: quick.date, hours, activity: quick.activity, description: quick.description, project_id: quick.project_id || '', contact_id: quick.contact_id || '', billable: quick.billable });
      toast.success(`Logged ${fmtHours(hours)}`);
      setQuick({ ...blankQuick(), date: quick.date, project_id: quick.project_id, contact_id: quick.contact_id, contact_name: quick.contact_name, activity: quick.activity });
      load();
    } catch (e) { err(e, 'Could not log it'); }
  };

  const days = useMemo(() => {
    const g = [];
    (data?.entries || []).forEach((e) => { const last = g[g.length - 1]; if (last && last.date === e.date) last.items.push(e); else g.push({ date: e.date, items: [e] }); });
    return g;
  }, [data]);
  const preview = rateFor(settings, quick.project_id, quick.contact_id);
  const s = data?.summary || {};

  return (
    <div className="space-y-4">
      <div className={`${card} p-3`}>
        <div className="grid grid-cols-2 md:grid-cols-[130px_90px_1fr_1.4fr_1fr_1fr_auto] gap-2 items-center">
          <Input type="date" value={quick.date} onChange={(e) => setQuick({ ...quick, date: e.target.value })} />
          <Input placeholder="1:30" value={quick.hoursText} onChange={(e) => setQuick({ ...quick, hoursText: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && add()} title="Hours: 1.5, 1:30 or 90m" />
          <Input list="time-activities" placeholder="Activity" value={quick.activity} onChange={(e) => setQuick({ ...quick, activity: e.target.value })} />
          <Input placeholder="What you did" value={quick.description} onChange={(e) => setQuick({ ...quick, description: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && add()} className="col-span-2 md:col-span-1" />
          <select className={sel} value={quick.project_id} onChange={(e) => setQuick({ ...quick, project_id: e.target.value })}>
            <option value="">No project</option>{projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
          <ContactSelect value={quick.contact_id} label={quick.contact_name} onChange={(c) => setQuick({ ...quick, contact_id: c?.id || '', contact_name: c?.label || '' })} />
          <Button className={red} onClick={add}><Plus className="w-4 h-4 mr-1" />Log</Button>
        </div>
        <div className="flex items-center gap-4 mt-2 text-xs text-[#5C6670]">
          <label className="flex items-center gap-1.5"><Switch checked={quick.billable} onCheckedChange={(v) => setQuick({ ...quick, billable: v })} />Billable</label>
          {quick.billable && <span>{preview.rate ? `${fmtMoney(preview.rate)}/h (${preview.source})` : 'No rate set yet. Add one under Rates.'}{parseHours(quick.hoursText) && preview.rate ? ` · ${fmtMoney(parseHours(quick.hoursText) * preview.rate)}` : ''}</span>}
          <span className="ml-auto hidden sm:inline">Or use the <Timer className="w-3 h-3 inline -mt-0.5" /> timer at the top of any page</span>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="Hours" value={fmtHours(s.hours)} note={`${fmtHours(s.non_billable_hours)} not billable`} />
        <Tile label="Billable" value={fmtHours(s.billable_hours)} note={fmtMoney(s.billable_amount)} />
        <Tile label="Not yet invoiced" value={fmtMoney(s.unbilled_amount)} note={fmtHours(s.unbilled_hours)} />
        <Tile label="Invoiced" value={fmtMoney(s.billed_amount)} />
      </div>

      <div className={`${card} overflow-hidden`}>
        <div className="flex items-center gap-2 flex-wrap p-3 border-b border-[#E3DDD1]">
          <PeriodPicker value={period} onChange={setPeriod} />
          <select className="h-9 rounded-md border border-input px-2 text-sm bg-white" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">All projects</option><option value="none">No project</option>{projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
          <select className="h-9 rounded-md border border-input px-2 text-sm bg-white" value={billable} onChange={(e) => setBillable(e.target.value)}>
            <option value="">Billable and not</option><option value="yes">Billable</option><option value="no">Not billable</option>
          </select>
          <label className="flex items-center gap-1.5 text-sm"><Switch checked={unbilled} onCheckedChange={setUnbilled} />Not yet invoiced</label>
          <div className="relative ml-auto w-full sm:w-52"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#94A3B8]" /><Input className="pl-8 h-9" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          <Button variant="ghost" size="sm" title="PDF of this period" onClick={async () => { try { const r = await timeAPI.reportPdf({ ...periodParams(period), project_id: projectId || undefined }); openBlob(r.data); } catch (e) { err(e, 'Could not make the PDF'); } }}><FileDown className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" title="Download CSV" onClick={async () => { const r = await timeAPI.exportCsv({ ...periodParams(period), project_id: projectId || undefined }); openBlob(r.data, 'time.csv'); }}><Download className="w-4 h-4" /></Button>
        </div>
        {!data ? <div className="p-6 text-sm text-[#5C6670]">Loading…</div> : days.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C6670]"><Timer className="w-8 h-8 mx-auto text-[#C9C0B0] mb-2" />No time logged for this period. Catching up on past weeks? Use the Catch up tab.</div>
        ) : days.map((d) => (
          <div key={d.date}>
            <div className="flex items-center px-3 py-1.5 bg-[#FBF8F2] border-t border-[#F0EBE1] text-xs font-semibold text-[#5C6670]">
              <span>{fmtDay(d.date, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
              <span className="ml-auto">{fmtHours(d.items.reduce((a, e) => a + e.hours, 0))}</span>
            </div>
            {d.items.map((e) => (
              <div key={e.id} className="flex items-center gap-3 px-3 py-2 border-t border-[#F0EBE1] hover:bg-[#FBF8F2] cursor-pointer" onClick={() => setEdit(e)}>
                <div className="w-16 text-sm font-semibold text-[#1E2A33] tabular-nums">{fmtHours(e.hours)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#1E2A33] truncate">{[e.activity, e.description].filter(Boolean).join(' · ') || <span className="text-[#94A3B8]">No note</span>}</div>
                  <div className="text-xs text-[#5C6670] truncate">{[e.project_name, e.contact_name, isAdmin() && e.user_name].filter(Boolean).join(' · ')}</div>
                </div>
                <div className="text-right shrink-0">
                  {e.billable ? <div className="text-sm">{fmtMoney(e.amount)}</div> : <div className="text-xs text-[#94A3B8]">Not billable</div>}
                  {e.invoice_number ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-50 text-sky-800">{e.applied ? 'Covered by' : 'Billed'} {e.invoice_number}</span>
                    : e.billable && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-800">To bill</span>}
                </div>
                <div className="flex shrink-0">
                  <button className="p-1 text-[#94A3B8] hover:text-[#1E2A33]" title="Log again today" onClick={async (ev) => {
                    ev.stopPropagation();
                    setQuick({ ...blankQuick(), activity: e.activity, description: e.description, project_id: e.project_id || '', contact_id: e.contact_id || '', contact_name: e.contact_name || '', billable: e.billable });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}><Copy className="w-3.5 h-3.5" /></button>
                  {!e.invoice_id && <button className="p-1 text-[#94A3B8] hover:text-[#A62B1F]" title="Delete" onClick={async (ev) => {
                    ev.stopPropagation(); if (!window.confirm('Delete this time?')) return;
                    try { await timeAPI.remove(e.id); load(); changed(); } catch (er) { err(er, 'Could not delete'); }
                  }}><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <EntryDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} entry={edit} settings={settings} onSaved={load} />
    </div>
  );
};

// ── Catch up: many rows at once ──────────────────────────────────────────────
const blankRow = (from) => ({ date: from?.date || todayISO(), hoursText: '', activity: from?.activity || '', description: '', project_id: from?.project_id || '', contact_id: from?.contact_id || '', contact_name: from?.contact_name || '', billable: from ? from.billable : true });

const CatchUp = ({ settings }) => {
  const navigate = useNavigate();
  const { projects = [] } = useProject() || {};
  const [rows, setRows] = useState(() => { const first = blankRow(); return [first, ...Array.from({ length: 4 }, () => blankRow(first))]; });
  const [busy, setBusy] = useState(false);
  const setRow = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = (i = rows.length - 1) => setRows([...rows.slice(0, i + 1), blankRow(rows[i]), ...rows.slice(i + 1)]);
  const filled = rows.filter((r) => r.hoursText.trim());
  const totals = filled.reduce((a, r) => {
    const h = parseHours(r.hoursText) || 0;
    a.hours += h; if (r.billable) a.amount += h * rateFor(settings, r.project_id, r.contact_id).rate; return a;
  }, { hours: 0, amount: 0 });

  const save = async () => {
    const bad = filled.findIndex((r) => !parseHours(r.hoursText));
    if (bad >= 0) return toast.error(`Row ${rows.indexOf(filled[bad]) + 1}: hours should look like 1.5, 1:30 or 90m`);
    setBusy(true);
    try {
      const r = await timeAPI.bulk(filled.map((x) => ({ date: x.date, hours: parseHours(x.hoursText), activity: x.activity, description: x.description,
        project_id: x.project_id || '', contact_id: x.contact_id || '', billable: x.billable })));
      toast.success(`Logged ${r.data.created} entries, ${fmtHours(r.data.hours)}${r.data.amount ? `, ${fmtMoney(r.data.amount)} billable` : ''}`);
      const last = filled[filled.length - 1];
      setRows([blankRow(last), blankRow(last), blankRow(last)]);
      changed();
    } catch (e) { err(e, 'Could not save'); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className={`${card} p-4 text-sm text-[#33404A]`}>
        <strong>Catching up on past work.</strong> One row per block of time. Each new row starts with the date, project, client and activity of the row above, so going through a week of emails is mostly typing hours and a note. Press Enter in the last row for another. Hours can be 1.5, 1:30 or 90m.
      </div>
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm min-w-[980px]">
          <thead className="text-[11px] uppercase tracking-wider text-[#5C6670] bg-[#FBF8F2]">
            <tr><th className="p-2 text-left w-[140px]">Date</th><th className="p-2 text-left w-[80px]">Hours</th><th className="p-2 text-left w-[160px]">Activity</th><th className="p-2 text-left">What you did</th>
              <th className="p-2 text-left w-[170px]">Project</th><th className="p-2 text-left w-[180px]">Client</th><th className="p-2 w-[60px]">Bill</th><th className="p-2 text-right w-[90px]">Amount</th><th className="w-[56px]" /></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const h = parseHours(r.hoursText);
              const rate = rateFor(settings, r.project_id, r.contact_id).rate;
              return (
                <tr key={i} className="border-t border-[#F0EBE1] align-top">
                  <td className="p-1"><Input type="date" value={r.date} onChange={(e) => setRow(i, { date: e.target.value })} /></td>
                  <td className="p-1"><Input value={r.hoursText} placeholder="0:00" className={r.hoursText && !h ? 'border-red-400' : ''} onChange={(e) => setRow(i, { hoursText: e.target.value })} /></td>
                  <td className="p-1"><Input list="time-activities" value={r.activity} onChange={(e) => setRow(i, { activity: e.target.value })} /></td>
                  <td className="p-1"><Input value={r.description} onChange={(e) => setRow(i, { description: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRow(i); } }} /></td>
                  <td className="p-1"><select className={sel} value={r.project_id} onChange={(e) => setRow(i, { project_id: e.target.value })}>
                    <option value="">None</option>{projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}</select></td>
                  <td className="p-1"><ContactSelect value={r.contact_id} label={r.contact_name} placeholder="Client" onChange={(c) => setRow(i, { contact_id: c?.id || '', contact_name: c?.label || '' })} /></td>
                  <td className="p-1 text-center"><div className="h-10 flex items-center justify-center"><Switch checked={r.billable} onCheckedChange={(v) => setRow(i, { billable: v })} /></div></td>
                  <td className="p-1 text-right"><div className="h-10 flex items-center justify-end text-[#5C6670]">{h && r.billable ? fmtMoney(h * rate) : ''}</div></td>
                  <td className="p-1"><div className="h-10 flex items-center">
                    <button className="p-1 text-[#94A3B8] hover:text-[#1E2A33]" title="Copy this row" onClick={() => setRows([...rows.slice(0, i + 1), { ...r }, ...rows.slice(i + 1)])}><Copy className="w-3.5 h-3.5" /></button>
                    <button className="p-1 text-[#94A3B8] hover:text-[#A62B1F]" title="Remove row" disabled={rows.length === 1} onClick={() => setRows(rows.filter((_, j) => j !== i))}><X className="w-3.5 h-3.5" /></button>
                  </div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="flex items-center gap-3 p-3 border-t border-[#E3DDD1] flex-wrap">
          <button className="text-xs font-semibold text-[#A62B1F]" onClick={() => addRow()}>+ Add a row</button>
          <button className="text-xs font-semibold text-[#A62B1F]" onClick={() => { const last = rows[rows.length - 1]; setRows([...rows, blankRow({ ...last, date: addDaysISO(last.date, 1) })]); }}>+ Next day</button>
          <span className="ml-auto text-sm text-[#5C6670]">{filled.length} row{filled.length === 1 ? '' : 's'} · {fmtHours(totals.hours)}{totals.amount ? ` · ${fmtMoney(totals.amount)} billable` : ''}</span>
          <Button className={red} disabled={busy || !filled.length} onClick={save}>{busy ? 'Saving…' : `Save ${filled.length || ''} entr${filled.length === 1 ? 'y' : 'ies'}`}</Button>
          <Button variant="ghost" onClick={() => navigate('/time')}>View timesheet</Button>
        </div>
      </div>
    </div>
  );
};

// ── Report ───────────────────────────────────────────────────────────────────
const GroupTable = ({ title, rows }) => (
  <div className={`${card} overflow-hidden`}>
    <div className="px-4 py-3 font-semibold text-[#1E2A33] border-b border-[#E3DDD1]">{title}</div>
    {rows.length === 0 ? <div className="p-4 text-sm text-[#5C6670]">Nothing yet.</div> : (
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase tracking-wider text-[#5C6670]"><tr><th className="text-left p-2 pl-4" /><th className="text-right p-2">Hours</th><th className="text-right p-2">Billable</th><th className="text-right p-2">Value</th><th className="text-right p-2 pr-4">Not invoiced</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.name} className="border-t border-[#F0EBE1]"><td className="p-2 pl-4">{r.name}</td><td className="p-2 text-right tabular-nums">{fmtHours(r.hours)}</td>
            <td className="p-2 text-right tabular-nums text-[#5C6670]">{fmtHours(r.billable_hours)}</td><td className="p-2 text-right">{fmtMoney(r.amount)}</td><td className="p-2 pr-4 text-right">{r.unbilled ? fmtMoney(r.unbilled) : ''}</td></tr>
        ))}</tbody>
      </table>
    )}
  </div>
);

const Report = () => {
  const { projects = [] } = useProject() || {};
  const [period, setPeriod] = useState({ period: 'this_month' });
  const [projectId, setProjectId] = useState('');
  const [client, setClient] = useState({ id: '', label: '' });
  const [amounts, setAmounts] = useState(true);
  const [r, setR] = useState(null);
  const params = { ...periodParams(period), project_id: projectId || undefined, contact_id: client.id || undefined };
  useEffect(() => { timeAPI.report(params).then((x) => setR(x.data)).catch((e) => err(e, 'Could not build the report')); }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  const s = r?.summary || {};
  const pdf = async () => { try { const x = await timeAPI.reportPdf({ ...params, amounts }); openBlob(x.data); } catch (e) { err(e, 'Could not make the PDF'); } };
  return (
    <div className="space-y-4">
      <div className={`${card} p-3 space-y-2`}>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodPicker value={period} onChange={setPeriod} />
          <select className="h-9 rounded-md border border-input px-2 text-sm bg-white" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">All projects</option>{projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
          <ContactSelect value={client.id} label={client.label} placeholder="All clients" className="w-56" onChange={(c) => setClient({ id: c?.id || '', label: c?.label || '' })} />
          <div className="ml-auto flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-sm" title="Turn off for a timesheet to send a client: hours only, no rates or dollar values"><Switch checked={amounts} onCheckedChange={setAmounts} />Show rates and $</label>
            <Button className={red} size="sm" onClick={pdf}><FileDown className="w-4 h-4 mr-1" />PDF</Button>
            <Button variant="outline" size="sm" onClick={async () => { const x = await timeAPI.exportCsv(params); openBlob(x.data, 'time.csv'); }}><Download className="w-4 h-4 mr-1" />CSV</Button>
          </div>
        </div>
        <div className="text-xs text-[#5C6670]">{r?.label}{client.id || projectId ? ' · Filtered to one client or project, the PDF is titled as a timesheet you can send them.' : ''}</div>
      </div>
      {!r ? <div className={`${card} p-6 text-sm text-[#5C6670]`}>Loading…</div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile label="Hours" value={fmtHours(s.hours)} />
            <Tile label="Billable share" value={s.hours ? `${Math.round((s.billable_hours / s.hours) * 100)}%` : '–'} note={`${fmtHours(s.billable_hours)} billable`} />
            <Tile label="Billable value" value={fmtMoney(s.billable_amount)} />
            <Tile label="Not yet invoiced" value={fmtMoney(s.unbilled_amount)} />
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <GroupTable title="By project" rows={r.by_project} />
            <GroupTable title="By client" rows={r.by_client} />
            <GroupTable title="By activity" rows={r.by_activity} />
            {r.by_person.length > 1 ? <GroupTable title="By person" rows={r.by_person} /> : r.by_month.length > 0 && (
              <div className={`${card} p-4`}>
                <div className="font-semibold text-[#1E2A33] mb-2">By month</div>
                {(() => { const max = Math.max(1, ...r.by_month.map((m) => m.hours)); return r.by_month.map((m) => (
                  <div key={m.month} className="flex items-center gap-2 text-sm py-0.5">
                    <span className="w-16 text-[#5C6670]">{fmtDay(`${m.month}-01`, { month: 'short', year: '2-digit' })}</span>
                    <div className="flex-1 h-2 rounded-full bg-[#F3EFE7] overflow-hidden relative">
                      <div className="absolute inset-y-0 left-0 bg-[#E3C9A8]" style={{ width: `${(m.hours / max) * 100}%` }} />
                      <div className="absolute inset-y-0 left-0 bg-[#A62B1F]" style={{ width: `${(m.billable_hours / max) * 100}%` }} />
                    </div>
                    <span className="w-16 text-right tabular-nums">{fmtHours(m.hours)}</span>
                  </div>)); })()}
                <p className="text-[11px] text-[#94A3B8] mt-2">Dark: billable. Light: everything else.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ── Rates ────────────────────────────────────────────────────────────────────
const Rates = ({ settings, onSaved }) => {
  const { projects = [] } = useProject() || {};
  const [f, setF] = useState(null);
  const [newRate, setNewRate] = useState({ scope: 'project', ref_id: '', name: '', rate: '' });
  useEffect(() => { if (settings) setF({ default_rate: settings.default_rate, rounding_minutes: settings.rounding_minutes, activities: (settings.activities || []).join('\n'), rates: settings.rates || [] }); }, [settings]);
  if (!f) return <div className={`${card} p-6 text-sm text-[#5C6670]`}>Loading…</div>;
  const save = async (patch = {}) => {
    const next = { ...f, ...patch };
    try {
      const r = await timeAPI.updateSettings({ default_rate: Number(next.default_rate || 0), rounding_minutes: Number(next.rounding_minutes), activities: next.activities.split('\n'),
        rates: next.rates.map((x) => ({ scope: x.scope, ref_id: x.ref_id, name: x.name || '', rate: Number(x.rate || 0) })) });
      onSaved(r.data); toast.success('Saved');
    } catch (e) { err(e, 'Could not save'); }
  };
  const editable = isAdmin();
  return (
    <div className="space-y-4 max-w-3xl">
      <div className={`${card} p-4 space-y-3`}>
        <h3 className="font-semibold text-[#1E2A33]">Hourly rates</h3>
        <p className="text-xs text-[#5C6670]">The project's rate is used if it has one, then the client's, then your default. Each entry keeps the rate it was logged at, so changing a rate here doesn't change past work.</p>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div><span className="text-[11px] text-[#5C6670]">Default rate ($/h)</span><Input type="number" step="0.01" disabled={!editable} value={f.default_rate} onChange={(e) => setF({ ...f, default_rate: e.target.value })} /></div>
          <div><span className="text-[11px] text-[#5C6670]">Round timers up to</span>
            <select className={sel} disabled={!editable} value={f.rounding_minutes} onChange={(e) => setF({ ...f, rounding_minutes: e.target.value })}>
              {[[0, 'Exact'], [1, '1 minute'], [5, '5 minutes'], [6, '6 minutes (0.1 h)'], [10, '10 minutes'], [15, '15 minutes'], [30, '30 minutes']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
        </div>
        <div className="divide-y divide-[#F0EBE1] border border-[#E3DDD1] rounded-lg">
          {f.rates.length === 0 && <div className="p-3 text-sm text-[#5C6670]">No project or client rates yet: everything uses the default.</div>}
          {f.rates.map((x, i) => (
            <div key={`${x.scope}-${x.ref_id}`} className="flex items-center gap-2 p-2 text-sm">
              <span className="text-[10px] uppercase tracking-wide font-semibold text-[#5C6670] w-14">{x.scope}</span>
              <span className="flex-1 truncate">{x.name}</span>
              <Input type="number" step="0.01" className="w-28 text-right" disabled={!editable} value={x.rate} onChange={(e) => setF({ ...f, rates: f.rates.map((y, j) => (j === i ? { ...y, rate: e.target.value } : y)) })} /><span className="text-xs text-[#5C6670]">/h</span>
              {editable && <button className="text-[#94A3B8] hover:text-[#A62B1F]" onClick={() => setF({ ...f, rates: f.rates.filter((_, j) => j !== i) })}><X className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
        {editable && (
          <div className="flex gap-2 items-end flex-wrap">
            <select className="h-10 rounded-md border border-input px-2 text-sm bg-white" value={newRate.scope} onChange={(e) => setNewRate({ scope: e.target.value, ref_id: '', name: '', rate: newRate.rate })}>
              <option value="project">Project</option><option value="client">Client</option></select>
            <div className="flex-1 min-w-[200px]">
              {newRate.scope === 'project' ? (
                <select className={sel} value={newRate.ref_id} onChange={(e) => setNewRate({ ...newRate, ref_id: e.target.value, name: projects.find((p) => p._id === e.target.value)?.name || '' })}>
                  <option value="">Choose a project…</option>{projects.filter((p) => !f.rates.some((x) => x.scope === 'project' && x.ref_id === p._id)).map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              ) : <ContactSelect value={newRate.ref_id} label={newRate.name} placeholder="Find a client…" onChange={(c) => setNewRate({ ...newRate, ref_id: c?.id || '', name: c?.label || '' })} />}
            </div>
            <Input type="number" step="0.01" placeholder="$/h" className="w-28" value={newRate.rate} onChange={(e) => setNewRate({ ...newRate, rate: e.target.value })} />
            <Button variant="outline" disabled={!newRate.ref_id || newRate.rate === ''} onClick={() => { setF({ ...f, rates: [...f.rates, newRate] }); setNewRate({ scope: newRate.scope, ref_id: '', name: '', rate: '' }); }}><Plus className="w-4 h-4 mr-1" />Add</Button>
          </div>
        )}
      </div>
      <div className={`${card} p-4 space-y-2`}>
        <h3 className="font-semibold text-[#1E2A33]">Activities</h3>
        <p className="text-xs text-[#5C6670]">Suggested as you type. One per line. On an invoice you can group hours by activity, e.g. "Prep & research, 10 h".</p>
        <textarea className="w-full rounded-md border border-input p-2 text-sm" rows={8} disabled={!editable} value={f.activities} onChange={(e) => setF({ ...f, activities: e.target.value })} />
      </div>
      {editable && <Button className={red} onClick={() => save()}>Save rates and activities</Button>}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const TABS = [['', 'Timesheet'], ['catch-up', 'Catch up'], ['report', 'Report'], ['rates', 'Rates']];

export default function Time() {
  const { tab = '' } = useParams();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  useEffect(() => { timeAPI.settings().then((r) => setSettings(r.data)).catch(() => {}); }, []);
  return (
    <Layout title="Time">
      <div className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-[#1E2A33] mr-auto" style={serif}>Time</h1>
          <div className="flex gap-1 bg-[#F3EFE7] rounded-lg p-1">
            {TABS.map(([k, l]) => <button key={k} onClick={() => navigate(k ? `/time/${k}` : '/time')} className={`text-sm px-3 py-1.5 rounded-md ${tab === k ? 'bg-white shadow-sm text-[#1E2A33] font-semibold' : 'text-[#5C6670]'}`}>{l}</button>)}
          </div>
        </div>
        <datalist id="time-activities">{(settings?.activities || []).map((a) => <option key={a} value={a} />)}</datalist>
        {tab === 'catch-up' ? <CatchUp settings={settings} /> : tab === 'report' ? <Report /> : tab === 'rates' ? <Rates settings={settings} onSaved={setSettings} /> : <Timesheet settings={settings} />}
      </div>
    </Layout>
  );
}
